import { useState } from 'react';
import axios from 'axios';
import { downloadBlob, downloadText } from '@/lib/download';
import { toast } from './useToast';

interface ImportProgress {
  isImporting: boolean;
  progress: number;
  message: string;
  errors: string[];
}

interface ExportProgress {
  isExporting: boolean;
  progress: number;
  message: string;
}

type ExportTransformResult = {
  content: string | Blob;
  mimeType?: string;
  extension?: string;
};

type ExportOptions = {
  params?: Record<string, string | number | boolean | undefined | null>;
  responseType?: 'json' | 'blob';
  transform?: (payload: any) => ExportTransformResult | null;
  emptyMessage?: string;
};

export function useImportExport() {
  const [importProgress, setImportProgress] = useState<ImportProgress>({
    isImporting: false,
    progress: 0,
    message: '',
    errors: [],
  });

  const [exportProgress, setExportProgress] = useState<ExportProgress>({
    isExporting: false,
    progress: 0,
    message: '',
  });

  const importData = async (
    file: File,
    endpoint: string,
    onSuccess?: (result: any) => void
  ) => {
    setImportProgress({
      isImporting: true,
      progress: 0,
      message: 'Preparing import...',
      errors: [],
    });

    try {
      const formData = new FormData();
      formData.append('file', file);

      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setImportProgress((prev) => ({
          ...prev,
          progress: Math.min(prev.progress + 10, 90),
          message: prev.progress < 30 ? 'Uploading file...' : 
                   prev.progress < 60 ? 'Validating data...' : 
                   'Processing records...',
        }));
      }, 500);

      const response = await axios.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      clearInterval(progressInterval);

      setImportProgress({
        isImporting: false,
        progress: 100,
        message: 'Import completed successfully',
        errors: [],
      });

      toast({
        title: 'Import successful',
        description: `Successfully imported ${response.data.imported || 0} records`,
      });

      if (onSuccess) {
        onSuccess(response.data);
      }

      // Reset after 3 seconds
      setTimeout(() => {
        setImportProgress({
          isImporting: false,
          progress: 0,
          message: '',
          errors: [],
        });
      }, 3000);

    } catch (error: any) {
      setImportProgress({
        isImporting: false,
        progress: 0,
        message: 'Import failed',
        errors: error.response?.data?.errors || [error.message],
      });

      toast({
        title: 'Import failed',
        description: error.response?.data?.error?.message || 'Failed to import data',
        variant: 'destructive',
      });
    }
  };

  const exportData = async (
    endpoint: string,
    filename: string,
    format: 'csv' | 'xlsx' | 'json' = 'xlsx',
    options: ExportOptions = {}
  ) => {
    setExportProgress({
      isExporting: true,
      progress: 0,
      message: 'Preparing export...',
    });

    let progressInterval: ReturnType<typeof setInterval> | null = null;
    try {
      const resolvedEndpoint = endpoint.endsWith('/export') ? endpoint : `${endpoint}/export`;
      const sanitizedParams = Object.fromEntries(
        Object.entries(options.params ?? {}).filter(
          ([_, value]) => value !== undefined && value !== null && value !== ''
        )
      );
      const responseType = options.responseType ?? (options.transform ? 'json' : 'blob');

      // Simulate progress updates
      progressInterval = setInterval(() => {
        setExportProgress((prev) => ({
          ...prev,
          progress: Math.min(prev.progress + 15, 90),
          message: prev.progress < 30 ? 'Fetching data...' : 
                   prev.progress < 60 ? 'Formatting data...' : 
                   'Generating file...',
        }));
      }, 300);

      const response = await axios.get(resolvedEndpoint, {
        params: { ...sanitizedParams, format },
        responseType,
      });

      if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
      }

      if (options.transform) {
        const payload = responseType === 'blob' ? response.data : response.data?.data ?? response.data;
        const transformed = options.transform(payload);

        if (!transformed) {
          setExportProgress({
            isExporting: false,
            progress: 0,
            message: '',
          });

          toast({
            title: 'No data to export',
            description: options.emptyMessage || 'No records match the current filters.',
          });
          return;
        }

        const extension = transformed.extension ?? format;
        if (transformed.content instanceof Blob) {
          downloadBlob(transformed.content, `${filename}.${extension}`);
        } else {
          downloadText(
            transformed.content,
            `${filename}.${extension}`,
            transformed.mimeType || 'text/plain;charset=utf-8;'
          );
        }
      } else if (responseType === 'blob') {
        downloadBlob(new Blob([response.data]), `${filename}.${format}`);
      } else {
        const jsonContent = JSON.stringify(response.data?.data ?? response.data, null, 2);
        downloadText(jsonContent, `${filename}.${format}`, 'application/json;charset=utf-8;');
      }

      setExportProgress({
        isExporting: false,
        progress: 100,
        message: 'Export completed successfully',
      });

      toast({
        title: 'Export successful',
        description: `File downloaded as ${filename}.${format}`,
      });

      // Reset after 3 seconds
      setTimeout(() => {
        setExportProgress({
          isExporting: false,
          progress: 0,
          message: '',
        });
      }, 3000);

    } catch (error: any) {
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      setExportProgress({
        isExporting: false,
        progress: 0,
        message: 'Export failed',
      });

      toast({
        title: 'Export failed',
        description: error.response?.data?.error?.message || 'Failed to export data',
        variant: 'destructive',
      });
    }
  };

  return {
    importData,
    exportData,
    importProgress,
    exportProgress,
  };
}
